/**
 * DecisionAnsweredCard — "answered" as a VISIBLE state, not an absence.
 *
 * emuw-v1-5 `shared-decision-components` Phase 3 task 8.
 *
 * The behaviour this replaces: the decision card silently vanished from under the
 * AM's cursor mid-read when somebody else answered. They were left wondering
 * whether they had missed something, whether they had clicked it themselves, or
 * whether the surface was broken. This card stays put and says what happened.
 *
 * TWO CALLERS, ONE CARD, AND THE DIFFERENCE MATTERS:
 *
 *   `refused` — THIS surface's own submit was refused. The AM clicked, and their
 *               click did not land. They must be told BOTH that it did not land
 *               and that nothing further will be sent, or they keep clicking.
 *   otherwise — the decision was answered somewhere else while this surface was
 *               showing it. Nobody here clicked anything; this is information,
 *               not a failure.
 *
 * IT MUST RENDER USABLY BEFORE THE ANSWER IS KNOWN, AND MUST RE-RENDER WHEN IT
 * ARRIVES. `answeredWith` is fetched from hitl-history on the same transition
 * that raises this card, so `null` on the first frame is the NORMAL case, not an
 * edge case. Two failure modes to avoid, both of which look fine in a snapshot:
 * rendering an empty quoted string (a generic error wearing punctuation), and
 * memoising the body on first render so the AM stays permanently on the
 * un-naming copy. The component is a pure function of its props precisely so the
 * second cannot happen.
 *
 * `role="status"` + `aria-live="polite"` — announced, but NOT `role="alert"`.
 * This is not an error, and an assertive interruption is the wrong register for a
 * state change the AM did not cause.
 *
 * Every visible string comes from `copy.ts`. No literal in this file — that is
 * what makes "same wording everywhere" checkable rather than a matter of taste.
 * Colours and spacing come from the host's own tokens; no hardcoded palette.
 */

import React from "react";

import {
  DECISION_ALREADY_ANSWERED_BODY_UNKNOWN,
  DECISION_ALREADY_ANSWERED_TITLE,
  decisionAlreadyAnsweredBody,
  decisionRefusedBody,
} from "../copy";

export interface DecisionAnsweredCardProps {
  /**
   * Plain-language rendering of the recorded answer, from `summarizeHitlResponse`
   * over the hitl-history event for this step. Produced by
   * `useAnsweredDecisionSummary`, never by the host. `null` while that fetch is in
   * flight — the card still renders.
   */
  answeredWith: string | null;
  /** True when THIS surface's own submission was refused. */
  refused?: boolean;
  answeredByName?: string | null;
  answeredAt?: string | null;
  className?: string;
}

export function DecisionAnsweredCard({
  answeredWith,
  refused = false,
  answeredByName = null,
  answeredAt = null,
  className = "",
}: DecisionAnsweredCardProps): React.ReactElement {
  // Both branches fall back to copy that says something true when the answer is
  // not known yet. Neither ever renders an empty quote.
  const body = refused
    ? decisionRefusedBody(answeredWith)
    : answeredWith
      ? decisionAlreadyAnsweredBody(answeredWith)
      : DECISION_ALREADY_ANSWERED_BODY_UNKNOWN;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="decision-answered-card"
      data-refused={refused ? "true" : "false"}
      data-answer-known={answeredWith ? "true" : "false"}
      className={`rounded-xl border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2 ${className}`}
    >
      <p className="text-sm font-semibold text-primary-gray-light-900">
        {DECISION_ALREADY_ANSWERED_TITLE}
      </p>
      <p className="mt-1 text-sm text-primary-gray-light-700">{body}</p>
      {(answeredByName || answeredAt) && (
        <p className="mt-1 text-xs text-primary-gray-light-500">
          {[answeredByName, answeredAt].filter(Boolean).join(" · ")}
        </p>
      )}
    </div>
  );
}

export default DecisionAnsweredCard;
